import type { ReactNode } from 'react';
import { Card, CardBody, NumberDisplay, Button, EmptyState } from '@aidkitorg/component-library';
import { UserGroupIcon } from '@heroicons/react/24/solid';
import { useLocalizedStrings } from '../../../Localization';

export type QueueCardProps = {
  name: string;
  description?: string;
  inQueue: number;
  inReview?: number;
  completed?: number;
  hasSubmitCompleteField: boolean;
  onClaimApplicant: () => void;
  isLoading?: boolean;
  /** When the queue's data failed to load, show an error instead of misleading zero counts. */
  loadError?: boolean;
  /** Optional content (e.g. the applicants table) rendered inside the card below the stats. */
  children?: ReactNode;
};

export const QueueCard = ({
  name,
  description,
  inQueue,
  inReview,
  completed,
  hasSubmitCompleteField,
  onClaimApplicant,
  isLoading = false,
  loadError = false,
  children,
}: QueueCardProps) => {
  const L = useLocalizedStrings();

  return (
    <Card className="rounded-lg h-full">
      <CardBody className="h-full min-h-0">
        <div className="flex flex-col h-full">
          {/* Queue Header */}
          <div className={`flex justify-between items-start ${!description ? 'mb-4' : ''}`}>
            <div >
              <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
              {description && (
                <p className="text-sm text-gray-600 mt-1" dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </div>
            <Button
              onClick={onClaimApplicant}
              isDisabled={inQueue === 0 || isLoading || loadError}
            >
              <div className="flex gap-2 items-center">
                <UserGroupIcon className="h-5 w-5" />
                {L.admin_panel.reviews.claim_next_applicant}
              </div>
            </Button>
          </div>

          {loadError ? (
            <EmptyState
              variant="widget"
              title={L.admin_panel.reviews.queue_load_error}
            />
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <NumberDisplay
                  loading={isLoading}
                  className="min-w-0"
                  title={L.admin_panel.reviews.in_this_queue}
                  value={inQueue}
                  iconName="ClockIcon"
                  iconColor="blue"
                />
                {hasSubmitCompleteField && inReview !== undefined && (
                  <NumberDisplay
                    loading={isLoading}
                    className="min-w-0"
                    title={L.admin_panel.reviews.in_review}
                    value={inReview}
                    iconName="ClockIcon"
                    iconColor="yellow"
                  />
                )}
                {hasSubmitCompleteField && completed !== undefined && (
                  <NumberDisplay
                    loading={isLoading}
                    className="min-w-0"
                    title={L.admin_panel.reviews.completed}
                    value={completed}
                    iconName="CheckCircleIcon"
                    iconColor="green"
                  />
                )}
              </div>

              {children && (
                <div className="mt-4 px-2 min-h-0 flex-1 flex flex-col">
                  {children}
                </div>
              )}
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
